import type {
  Amenity,
  CreateAmenityRequest,
  UpdateAmenityRequest,
} from '@/types/v2/amenity';
import axios from '@/lib/v2/axios';

export async function getAmenities(
  communityId: string,
  params: {
    include?: string;
    filter?: Record<string, any>;
    sort?: string;
    /** Pass true from resident-facing reservation flows to hide inactive amenities. Omit for settings/admin views. */
    activeOnly?: boolean;
  } = {},
): Promise<{ amenities: Amenity[] }> {
  const { activeOnly, ...rest } = params;
  const response = await axios.get(`/api/v2/communities/${communityId}/amenities`, {
    params: { ...rest, ...(activeOnly ? { active_only: 1 } : {}) },
  });
  return response.data;
}

export async function getAmenity(
  communityId: string,
  amenityId: string,
  params: { include?: string } = {},
): Promise<Amenity> {
  try {
    const response = await axios.get(
      `/api/v2/communities/${communityId}/amenities/${amenityId}`,
      { params },
    );
    // Laravel JsonResource wraps in "data" key; unwrap if present
    const body = response.data;
    return (body?.data ?? body) as Amenity;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Amenity not found');
    }
    throw error;
  }
}

export async function createAmenity(
  communityId: string,
  data: CreateAmenityRequest,
): Promise<Amenity> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/amenities`,
    data,
  );
  return response.data;
}

export async function updateAmenity(
  communityId: string,
  amenityId: string,
  data: Partial<UpdateAmenityRequest>,
): Promise<Amenity> {
  const response = await axios.put(
    `/api/v2/communities/${communityId}/amenities/${amenityId}`,
    data,
  );
  return response.data;
}

export async function deleteAmenity(
  communityId: string,
  amenityId: string,
): Promise<void> {
  await axios.delete(`/api/v2/communities/${communityId}/amenities/${amenityId}`);
}

